export const ACCOUNT_DELETION_SESSION_KEYS = {
  PENDING: 'account_deletion_pending',
  EMAIL: 'deletion_email',
  TIMESTAMP: 'deletion_timestamp',
} as const

export const ACCOUNT_DELETION_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

type DeletionSession = {
  isPending: boolean
  email: string | null
  timestamp: number | null
}

const safeSessionStorageGet = (key: string) => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return sessionStorage.getItem(key)
  } catch (error) {
    console.error('Failed to read sessionStorage key:', key, error)
    return null
  }
}

const safeSessionStorageSet = (key: string, value: string) => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    sessionStorage.setItem(key, value)
  } catch (error) {
    console.error('Failed to set sessionStorage key:', key, error)
  }
}

const safeSessionStorageRemove = (key: string) => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    sessionStorage.removeItem(key)
  } catch (error) {
    console.error('Failed to remove sessionStorage key:', key, error)
  }
}

const safeSessionStorageClear = () => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    sessionStorage.clear()
  } catch (error) {
    console.error('Failed to clear sessionStorage:', error)
  }
}

export const storeDeletionIntent = (email: string, timestamp = Date.now()) => {
  safeSessionStorageSet(ACCOUNT_DELETION_SESSION_KEYS.PENDING, 'true')
  safeSessionStorageSet(ACCOUNT_DELETION_SESSION_KEYS.EMAIL, email)
  safeSessionStorageSet(
    ACCOUNT_DELETION_SESSION_KEYS.TIMESTAMP,
    timestamp.toString()
  )
}

export const clearDeletionIntent = () => {
  safeSessionStorageRemove(ACCOUNT_DELETION_SESSION_KEYS.PENDING)
  safeSessionStorageRemove(ACCOUNT_DELETION_SESSION_KEYS.EMAIL)
  safeSessionStorageRemove(ACCOUNT_DELETION_SESSION_KEYS.TIMESTAMP)
}

export const clearAllSessionStorage = () => {
  safeSessionStorageClear()
}

export const readDeletionSession = (): DeletionSession => {
  const isPending =
    safeSessionStorageGet(ACCOUNT_DELETION_SESSION_KEYS.PENDING) === 'true'
  const email = safeSessionStorageGet(ACCOUNT_DELETION_SESSION_KEYS.EMAIL)
  const timestampRaw = safeSessionStorageGet(
    ACCOUNT_DELETION_SESSION_KEYS.TIMESTAMP
  )

  return {
    isPending,
    email,
    timestamp: timestampRaw ? Number(timestampRaw) : null,
  }
}

export const hasActiveDeletionIntent = () => {
  const { isPending, email, timestamp } = readDeletionSession()
  return isPending && Boolean(email) && Boolean(timestamp)
}

export const isDeletionSessionValid = (
  timeoutMs = ACCOUNT_DELETION_TIMEOUT_MS,
  currentEmail?: string
) => {
  const session = readDeletionSession()

  if (!session.isPending || !session.email || !session.timestamp) {
    return false
  }

  const elapsed = Date.now() - session.timestamp
  if (elapsed > timeoutMs) {
    clearDeletionIntent()
    return false
  }

  if (currentEmail && currentEmail !== session.email) {
    return false
  }

  return true
}
